// src/utils/fecha-art.js — helpers de formato de fecha/hora en hora Argentina.
//
// FASE 4 · DT-BL (zona horaria). Hasta este bloque cada función de formateo
// usaba métodos del navegador (getHours/getDate/toLocaleDateString sin
// timeZone), por lo que un usuario en otra TZ veía partidos desplazados —
// p. ej. un partido del 12-jun 00:00 UTC se mostraba como "11-jun 21:00" en
// ART y la app lo agrupaba en el día equivocado.
//
// La app es local de Argentina hasta que se prenda la UI de extranjero
// (27-nov); la hora se FIJA en ART (America/Argentina/Buenos_Aires) para
// todos los usuarios sin importar la TZ del navegador.
//
// Implementación con `Intl.DateTimeFormat` + `timeZone` explícito — es la
// opción estándar de Intl que sí respeta zona horaria, a diferencia de los
// métodos `Date.prototype.getXxx` que siempre usan la TZ del runtime.

const ART_TZ = 'America/Argentina/Buenos_Aires';
const LOCALE = 'es-AR';

const fechaCortaFmt = new Intl.DateTimeFormat(LOCALE, {
    timeZone: ART_TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
});

const horaFmt = new Intl.DateTimeFormat(LOCALE, {
    timeZone: ART_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});

const fechaLargaFmt = new Intl.DateTimeFormat(LOCALE, {
    timeZone: ART_TZ,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
});

// en-CA devuelve el formato ISO YYYY-MM-DD nativamente — truco estándar para
// obtener una key de día estable y comparable como string.
const fechaKeyFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: ART_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

// Normaliza input (Date | string ISO | number ms). Devuelve null si no parsea.
const toDate = (input) => {
    if (input == null) return null;
    if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
};

const stripDot = (s) => (s || '').replace(/\.$/, '');
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const partsToShortFecha = (d) => {
    const parts = fechaCortaFmt.formatToParts(d);
    const weekday = stripDot(parts.find((p) => p.type === 'weekday')?.value);
    const day = parts.find((p) => p.type === 'day')?.value || '';
    const month = stripDot(parts.find((p) => p.type === 'month')?.value);
    return `${capitalize(weekday)} ${day} ${month}`;
};

const partsToLongFecha = (d) => {
    const parts = fechaLargaFmt.formatToParts(d);
    const weekday = parts.find((p) => p.type === 'weekday')?.value || '';
    const day = parts.find((p) => p.type === 'day')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    return `${capitalize(weekday)} ${day} de ${month}`;
};

// "Mié 11 jun" — día con weekday abreviado, en ART.
export const formatFechaART = (input) => {
    const d = toDate(input);
    return d ? partsToShortFecha(d) : '';
};

// "16:00" — 24h en ART.
export const formatHoraART = (input) => {
    const d = toDate(input);
    return d ? horaFmt.format(d) : '';
};

// "Mié 11 jun · 16:00" — combinado en ART.
export const formatFechaHoraART = (input) => {
    const d = toDate(input);
    return d ? `${partsToShortFecha(d)} · ${horaFmt.format(d)}` : '';
};

// "Jueves 11 de junio" — formato largo en ART (capitalizado).
export const formatFechaLargaART = (input) => {
    const d = toDate(input);
    return d ? partsToLongFecha(d) : '';
};

// "YYYY-MM-DD" — key de día calendario ART, estable y comparable como string.
export const fechaKeyART = (input) => {
    const d = toDate(input);
    return d ? fechaKeyFmt.format(d) : '';
};

// True si dos inputs caen en el mismo día calendario ART.
export const mismoDiaART = (a, b) => {
    const ka = fechaKeyART(a);
    const kb = fechaKeyART(b);
    return Boolean(ka) && ka === kb;
};

// True si el input es hoy en ART (referencia: Date.now() del runtime).
export const esHoyART = (input) => mismoDiaART(input, new Date());
